/* global React, ReactDOM, CONTENT, PROJECTS, REVIEWS_BANK,
   Splash, Nav, Hero, Bento, Bio, Comunidad, Reviews, Metrics,
   NaturalDesborde, Footer, DesbordePanel, DesbordeTab, AboutPage,
   CaseDJY, CaseDJYReviewsPanel, CaseDJYMore, CaseALE, ContratameModal */

const { useState, useEffect, useRef } = React;

const LANG_STORAGE_KEY = "anitasevillaull.lang";

// ── Hash-based routing ──────────────────────────────────────────────────
// Each screen has its own URL hash so refreshing keeps you where you were.
const SCREEN_TO_HASH = {
  splash: "",
  home: "#home",
  about: "#sobre-mi",
  "case-djy": "#caso-djy",
  "case-djy-more": "#caso-djy/mas",
  "case-ale": "#caso-agente-ia",
};
const HASH_TO_SCREEN = {
  "": "splash",
  "#home": "home",
  "#sobre-mi": "about",
  "#caso-djy": "case-djy",
  "#caso-djy/mas": "case-djy-more",
  "#caso-agente-ia": "case-ale",
};
function readScreenFromHash() {
  const h = (typeof window !== "undefined" && window.location.hash) || "";
  if (h === "") return "splash";
  return HASH_TO_SCREEN[h] || "home";
}

function detectInitialLang() {
  try {
    const saved = localStorage.getItem(LANG_STORAGE_KEY);
    if (saved === "es" || saved === "en") return saved;
  } catch (_) {}
  const nav = (navigator.language || navigator.languages?.[0] || "es").toLowerCase();
  return nav.startsWith("es") ? "es" : "en";
}

// ── Feature flag: el "desborde" (VSL del home + tab lateral + auto-open
//   al llegar al bio + CTA "O conocéme en 90s" en bio) está apagado hasta
//   que esté grabado/editado el video. Cambiar a `true` para recuperar
//   todo el flow sin tocar nada más. */
const DESBORDE_PUBLISHED = false;

function App() {
  const [screen, setScreen] = useState(readScreenFromHash); // splash | home | process | case-djy | case-djy-more
  const [profile, setProfile] = useState("neutral");
  const [lang, setLangState] = useState(detectInitialLang);
  const [desbordeOpen, setDesbordeOpen] = useState(false);
  const [desbordeMode, setDesbordeMode] = useState("vsl");
  const [hasOpenedDesborde, setHasOpenedDesborde] = useState(false);
  const [desbordeIsAuto, setDesbordeIsAuto] = useState(false);
  const [caseDesbordeOpen, setCaseDesbordeOpen] = useState(false);
  const [caseDesbordeOpened, setCaseDesbordeOpened] = useState(false);
  const [caseDesbordeIsAuto, setCaseDesbordeIsAuto] = useState(false);

  const content = CONTENT[lang];
  const desbordeReturnFocusRef = useRef(null);
  const desbordeFirstFocusRef = useRef(null);
  const caseDesbordeReturnFocusRef = useRef(null);

  // ── Language: keep <html lang> in sync + persist to localStorage ──
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem(LANG_STORAGE_KEY, l); } catch (_) {}
  };

  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  // ── Sync screen → URL hash so refresh lands in the same place ──
  useEffect(() => {
    const desired = SCREEN_TO_HASH[screen] ?? "";
    const current = window.location.hash || "";
    if (current === desired) return;
    const url = desired
      ? window.location.pathname + window.location.search + desired
      : window.location.pathname + window.location.search;
    window.history.replaceState(null, "", url);
  }, [screen]);

  // ── React to browser back/forward and manual hash edits ──
  useEffect(() => {
    const onHashChange = () => {
      const next = readScreenFromHash();
      setScreen((prev) => {
        // Solo resetear el scroll cuando efectivamente cambiamos de pantalla
        // (no en hashes nuevos hacia la misma screen). Así el "Ver otro caso"
        // siempre arranca desde el inicio del nuevo caso.
        if (prev !== next) {
          window.scrollTo({ top: 0, behavior: "instant" });
        }
        return prev === next ? prev : next;
      });
    };
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("popstate", onHashChange);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("popstate", onHashChange);
    };
  }, []);

  // ── Splash auto-advance directly to home ──
  useEffect(() => {
    if (screen !== "splash") return;
    const id = setTimeout(() => setScreen("home"), 5200);
    return () => clearTimeout(id);
  }, [screen]);

  // ── Auto-open desborde when reaching Bio (home) ──
  useEffect(() => {
    if (!DESBORDE_PUBLISHED) return;
    if (screen !== "home") return;
    if (hasOpenedDesborde) return;
    const bio = document.getElementById("bio");
    if (!bio) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !hasOpenedDesborde) {
          setDesbordeOpen(true);
          setHasOpenedDesborde(true);
          setDesbordeIsAuto(true);
          io.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(bio);
    return () => io.disconnect();
  }, [screen, hasOpenedDesborde]);

  // ── Esc closes desborde panel (home + case) ──
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (desbordeOpen) {
        setDesbordeOpen(false);
        if (desbordeReturnFocusRef.current) desbordeReturnFocusRef.current.focus();
      }
      if (caseDesbordeOpen) {
        setCaseDesbordeOpen(false);
        if (caseDesbordeReturnFocusRef.current) caseDesbordeReturnFocusRef.current.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [desbordeOpen, caseDesbordeOpen]);

  // ── Move focus into the desborde panel when it opens ──
  useEffect(() => {
    if (!desbordeOpen) return;
    // wait a tick for the panel content to render
    const id = setTimeout(() => {
      const closeBtn = document.querySelector(".desborde-col .desborde-x");
      if (closeBtn) closeBtn.focus();
    }, 100);
    return () => clearTimeout(id);
  }, [desbordeOpen]);

  useEffect(() => {
    if (!caseDesbordeOpen) return;
    const id = setTimeout(() => {
      const closeBtn = document.querySelector(".desborde-col .desborde-x");
      if (closeBtn) closeBtn.focus();
    }, 100);
    return () => clearTimeout(id);
  }, [caseDesbordeOpen]);

  const handleOpenDesborde = (mode = "vsl", e) => {
    if (e && e.currentTarget) desbordeReturnFocusRef.current = e.currentTarget;
    setDesbordeMode(mode);
    setDesbordeOpen(true);
    setHasOpenedDesborde(true);
    setDesbordeIsAuto(false);
  };

  const handleCloseDesborde = () => {
    setDesbordeOpen(false);
    if (desbordeReturnFocusRef.current) desbordeReturnFocusRef.current.focus();
  };

  if (screen === "splash") {
    return <Splash content={content.splash} lang={lang} />;
  }

  if (screen === "about") {
    return (
      <div className="app-shell">
        <div className="main-col">
          <main id="main-content" role="main">
            <AboutPage
              content={content.home}
              lang={lang}
              setLang={setLang}
              onBack={() => { setScreen("home"); window.scrollTo({ top: 0, behavior: "instant" }); }}
            />
          </main>
        </div>
      </div>
    );
  }

  if (screen === "case-djy") {
    const close = () => {
      setCaseDesbordeOpen(false);
      if (caseDesbordeReturnFocusRef.current) caseDesbordeReturnFocusRef.current.focus();
    };
    const openReviews = (e) => {
      const isAuto = !(e && e.currentTarget);
      if (e && e.currentTarget) caseDesbordeReturnFocusRef.current = e.currentTarget;
      setCaseDesbordeOpen(true);
      setCaseDesbordeOpened(true);
      setCaseDesbordeIsAuto(isAuto);
    };
    const openMore = () => {
      setScreen("case-djy-more");
      window.scrollTo({ top: 0, behavior: "instant" });
    };
    return (
      <div className={`app-shell ${DESBORDE_PUBLISHED && caseDesbordeOpen ? "is-expanded" : ""} ${DESBORDE_PUBLISHED && caseDesbordeOpen && caseDesbordeIsAuto ? "is-auto-open" : ""}`}>
        <div className="main-col">
          <main id="main-content" role="main">
            <CaseDJY
              onBack={() => { setScreen("home"); window.scrollTo({ top: 0, behavior: "instant" }); }}
              onOpenReviews={DESBORDE_PUBLISHED ? openReviews : undefined}
              onOpenMore={openMore}
              reviewsOpened={caseDesbordeOpened}
              lang={lang}
              setLang={setLang}
            />
          </main>
        </div>
        {DESBORDE_PUBLISHED && !caseDesbordeOpen && (
          <DesbordeTab label={content.desborde.caseDjyTabLabel || "Lo que dijo el equipo · DJY"} onClick={openReviews} />
        )}
        {DESBORDE_PUBLISHED && (
          <aside
            className="desborde-col"
            role="complementary"
            aria-label={content.desborde.caseDjyAsideAria || "Reviews del equipo DJY Pro"}
            aria-hidden={!caseDesbordeOpen}
          >
            {caseDesbordeOpen && <CaseDJYReviewsPanel onClose={close} lang={lang} />}
          </aside>
        )}
      </div>
    );
  }

  if (screen === "case-djy-more") {
    return (
      <div className="app-shell">
        <div className="main-col">
          <main id="main-content" role="main">
            <CaseDJYMore
              onBack={() => { setScreen("home"); window.scrollTo({ top: 0, behavior: "instant" }); }}
              onBackToCase={() => { setScreen("case-djy"); window.scrollTo({ top: 0, behavior: "instant" }); }}
              lang={lang}
              setLang={setLang}
            />
          </main>
        </div>
      </div>
    );
  }

  if (screen === "case-ale") {
    return (
      <div className="app-shell">
        <div className="main-col">
          <main id="main-content" role="main">
            <CaseALE
              onBack={() => { setScreen("home"); window.scrollTo({ top: 0, behavior: "instant" }); }}
              lang={lang}
              setLang={setLang}
            />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={`app-shell ${desbordeOpen ? "is-expanded" : ""} ${desbordeOpen && desbordeIsAuto ? "is-auto-open" : ""}`}>
      <div className="main-col">
        <Nav
          lang={lang}
          setLang={setLang}
          content={content}
          onOpenDesborde={DESBORDE_PUBLISHED ? (e) => handleOpenDesborde("vsl", e) : undefined}
          isDesbordeOpen={desbordeOpen}
        />
        <main id="main-content" role="main">
          <Hero profile={profile} content={content.home} />
          <Bento
            profile={profile}
            content={content.home}
            projects={PROJECTS}
            lang={lang}
            onOpenCase={(id) => {
              if (id === "djy") {
                setCaseDesbordeOpen(false);
                setCaseDesbordeOpened(false);
                setScreen("case-djy");
                window.scrollTo({ top: 0, behavior: "instant" });
              } else if (id === "ale") {
                setScreen("case-ale");
                window.scrollTo({ top: 0, behavior: "instant" });
              }
            }}
          />
          <Bio
            profile={profile}
            content={content.home}
            onOpenAbout={() => { setScreen("about"); window.scrollTo({ top: 0, behavior: "instant" }); }}
            onOpenDesborde={DESBORDE_PUBLISHED ? (e) => handleOpenDesborde("vsl", e) : undefined}
          />
          <Comunidad content={content.home} />
          <Reviews content={content} reviews={REVIEWS_BANK} lang={lang} />
          {/* Métricas del portfolio ocultas en esta versión —
              descomentar cuando se quieran exponer.
          <Metrics content={content.home} />
          */}
          {DESBORDE_PUBLISHED && !hasOpenedDesborde && (
            <NaturalDesborde content={content.home} onOpenDesborde={(e) => handleOpenDesborde("vsl", e)} />
          )}
        </main>
        <Footer content={content.home} />
      </div>

      {DESBORDE_PUBLISHED && !desbordeOpen && (
        <DesbordeTab label={content.desborde.tabLabel} onClick={(e) => handleOpenDesborde("vsl", e)} />
      )}

      {DESBORDE_PUBLISHED && (
        <aside
          className="desborde-col"
          role="complementary"
          aria-label={desbordeMode === "vsl"
            ? (content.desborde.asideAriaVsl || "Video de presentación")
            : (content.desborde.asideAriaReviews || "Reviews")}
          aria-hidden={!desbordeOpen}
        >
          {desbordeOpen && (
            <DesbordePanel
              content={content.desborde}
              mode={desbordeMode}
              onClose={handleCloseDesborde}
              reviews={REVIEWS_BANK}
            />
          )}
        </aside>
      )}
    </div>
  );
}

function Root() {
  return (
    <React.Fragment>
      <App />
      {/* Modal global "Contratame" — escucha clicks de cualquier CTA con
          ese texto / mailto y se monta encima de cualquier pantalla. */}
      <ContratameModal />
    </React.Fragment>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<Root />);
